import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Megaphone, TrendingUp, Check } from 'lucide-react';
import { PageHeader } from '@/shared/ui/PageHeader';
import { EmptyState, Skeleton } from '@/shared/ui';
import { useLocation } from '@/shared/hooks/useLocation';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { lifecycleApi } from '../api/lifecycleApi';
import { strategyApi } from '../api/strategyApi';
import { strategyKeys } from '../queryKeys';
import { computeChannelMix, type ChannelShare } from '../utils/channelMix';
import styles from './ChannelMixPage.module.css';
import { monthlyBudgetOf } from '../utils/strategyBudget';

function fmtMoney(n: number): string {
  return `$${Math.round(n).toLocaleString()}`;
}

function ChannelBar({ channels, fillClass }: { channels: ChannelShare[]; fillClass: string }) {
  if (channels.length === 0) {
    return <p className={styles.splitNote}>No budget in this bucket.</p>;
  }
  return (
    <div>
      {channels.map(({ key, label, pct, monthly }) => (
        <div key={key} className={styles.channelRow}>
          <div className={styles.channelRowHeader}>
            <span>{label}</span>
            <span className={styles.channelRowPct}>
              {Math.round(pct)}% · {fmtMoney(monthly)}/mo
            </span>
          </div>
          <div className={styles.barTrack}>
            {/* width is data-driven and cannot be expressed as a static CSS class */}
            <div className={fillClass} style={{ width: `${pct}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ChannelMixPage() {
  const navigate = useNavigate();
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';

  const lifecycleQuery = useQuery({
    queryKey: strategyKeys.lifecycle(locationId),
    queryFn: () => lifecycleApi.getLifecycle(locationId),
    enabled: !!locationId,
  });
  const activeVersion = lifecycleQuery.data?.data?.active?.version;

  const insightsQuery = useQuery({
    queryKey: strategyKeys.insights(locationId, activeVersion),
    queryFn: () => strategyApi.getInsights(locationId, activeVersion),
    enabled: !!locationId && activeVersion !== undefined,
  });
  const insights = insightsQuery.data?.data ?? null;

  const mix = useMemo(
    () =>
      insights
        ? computeChannelMix(insights.strategies ?? [], insights.approvedIndexes ?? [])
        : null,
    [insights],
  );

  const header = (
    <PageHeader
      title="Channel Mix"
      subtitle="How your active strategy splits its recommended budget between Paid and Organic & Offline."
    />
  );

  if (lifecycleQuery.isLoading || insightsQuery.isLoading) {
    return (
      <div className={patterns.page}>
        {header}
        <Skeleton height={160} borderRadius="10px" />
      </div>
    );
  }

  if (lifecycleQuery.isError || insightsQuery.isError) {
    return (
      <div className={patterns.page}>
        {header}
        <EmptyState
          title="Couldn't load your channel mix"
          description="Something went wrong loading your strategy. Try again in a moment."
          action={{
            label: 'Retry',
            onClick: () => {
              void lifecycleQuery.refetch();
              void insightsQuery.refetch();
            },
          }}
        />
      </div>
    );
  }

  if (activeVersion === undefined || !mix || mix.totalMonthly <= 0) {
    return (
      <div className={patterns.page}>
        {header}
        <EmptyState
          title="No channel mix yet"
          description={
            activeVersion === undefined
              ? 'Activate a strategy to see how its budget splits across channels.'
              : 'None of the approved strategies carries a channel budget yet. Approve a paid or organic strategy to see the mix.'
          }
          action={{ label: 'Go to Strategy', onClick: () => navigate('/strategy') }}
        />
      </div>
    );
  }

  const paid = mix.channels.filter((c) => c.bucket === 'paid');
  const organic = mix.channels.filter((c) => c.bucket === 'organic');
  const paidPct = Math.round(mix.paidPct);
  const organicPct = 100 - paidPct;

  return (
    <div className={patterns.page}>
      {header}

      <div className={patterns.panel}>
        <div className={styles.derivedNotice}>
          <span className={styles.derivedIcon}>
            <Check size={14} aria-hidden />
          </span>
          Based on {mix.fundedStrategies.length} approved{' '}
          {mix.fundedStrategies.length === 1 ? 'strategy' : 'strategies'} in your active strategy
          (version {activeVersion}). Approve more strategies or rebuild the strategy and this
          updates.
        </div>
      </div>

      <div className={patterns.panel}>
        <div className={styles.maturityRow}>
          <div className={styles.maturityLeft}>
            <div>
              <div className={styles.maturityTitle}>Recommended monthly budget</div>
              <div className={styles.maturitySub}>
                Sum of the approved strategies' recommendations — you set the actual budget.
              </div>
            </div>
          </div>
          <div className={styles.investmentRight}>
            <div className={styles.investmentValue}>{fmtMoney(mix.totalMonthly)}/mo</div>
          </div>
        </div>

        <div className={styles.splitBar}>
          {paidPct > 0 && (
            <div
              className={`${styles.splitSegment} ${styles.splitSegmentPaid}`}
              // width is data-driven and cannot be expressed as a static CSS class
              style={{ width: `${paidPct}%` }}
            >
              Paid · {paidPct}%
            </div>
          )}
          {organicPct > 0 && (
            <div
              className={`${styles.splitSegment} ${styles.splitSegmentOrganic}`}
              style={{ width: `${organicPct}%` }}
            >
              Organic &amp; Offline · {organicPct}%
            </div>
          )}
        </div>
        <p className={styles.splitNote}>
          These two buckets map to your Paid and Organic &amp; Offline nav sections. Each strategy's
          budget is split evenly across the channels it uses.
        </p>
      </div>

      <div className={styles.channelGrid}>
        <div className={patterns.panel}>
          <div className={`${styles.channelGroupLabel} ${styles.channelGroupLabelPaid}`}>
            <Megaphone size={15} aria-hidden />
            Paid · {paidPct}%
          </div>
          <ChannelBar channels={paid} fillClass={styles.barFillPaid} />
        </div>
        <div className={patterns.panel}>
          <div className={`${styles.channelGroupLabel} ${styles.channelGroupLabelOrganic}`}>
            <TrendingUp size={15} aria-hidden />
            Organic &amp; Offline · {organicPct}%
          </div>
          <ChannelBar channels={organic} fillClass={styles.barFillOrganic} />
        </div>
      </div>

      <div className={patterns.panel}>
        <div className={styles.whyHeader}>Where this comes from</div>
        <ul className={styles.whyBody}>
          {mix.fundedStrategies.map((s) => (
            <li key={s.title}>
              <strong>{s.title}</strong> — {fmtMoney(monthlyBudgetOf(s))}/mo across{' '}
              {s.primary_channels.filter((c) => c !== 'operational').length} channel
              {s.primary_channels.filter((c) => c !== 'operational').length === 1 ? '' : 's'}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
